import * as Comlink from 'comlink';
import { IDdoc, Update } from '@/types';
import type {
  ExtendedDoc,
  GenerateEncryptionMaterialParams,
  GenerateEncryptionMaterialResult,
  WorkerAPI,
} from './types';
import { Lock } from '@/services/get-ddocs-by-portal-address';
import { KeyStoreInstance } from '../identity-utils/key-store';
import { AgentInstance } from '../identity-utils/smart-account-agent';
export class WorkerInstance {
  private static worker: Worker | null = null;
  private static workerApi: Comlink.Remote<WorkerAPI> | null = null;
  private static cleanupListenerAdded = false;
  private static operationCount = 0;
  private static readonly OPERATIONS_THRESHOLD = 1000;
  private static initializationPromise: Promise<void> | null = null;

  private static setupCleanupListeners() {
    if (typeof window === 'undefined' || this.cleanupListenerAdded) return;
    window.addEventListener('beforeunload', () => {
      this.dispose();
    });
    this.cleanupListenerAdded = true;
  }

  private static async initialize() {
    if (!this.initializationPromise) {
      this.initializationPromise = new Promise((resolve) => {
        this.worker = new Worker(new URL('./worker.ts', import.meta.url));
        this.workerApi = Comlink.wrap<WorkerAPI>(this.worker);
        this.setupCleanupListeners();
        this.operationCount = 0;

        // Wait for the worker to signal it's ready
        const onMessage = (event: MessageEvent) => {
          if (event.data === 'worker-ready') {
            this.worker!.removeEventListener('message', onMessage);
            console.log('✅ Worker is now active and ready to be used.');
            resolve(undefined);
          }
        };

        this.worker.addEventListener('message', onMessage);

        // Explicitly tell the worker to initialize
        this.worker.postMessage('initialize-worker');
      });
    }
    return this.initializationPromise;
  }

  private static async getWorker(): Promise<Comlink.Remote<WorkerAPI>> {
    if (
      !this.worker ||
      !this.workerApi ||
      this.operationCount >= this.OPERATIONS_THRESHOLD
    ) {
      if (this.worker) {
        this.cleanup();
      }
      await this.initialize();
    }
    this.operationCount++;
    return this.workerApi!;
  }

  private static cleanup(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.workerApi = null;
      window.removeEventListener('beforeunload', () => {
        this.dispose();
      });
    }
    this.operationCount = 0;
    this.cleanupListenerAdded = false;
    this.initializationPromise = null;
  }

  static async decryptDdocItem(
    ddocItem: Partial<IDdoc>,
    userKeys: any
  ): Promise<IDdoc> {
    if (!ddocItem) return ddocItem;
    const worker = await this.getWorker();
    const item = await worker.decryptDdocItem(ddocItem, userKeys);
    return Comlink.transfer(item, [ddocItem as unknown as Transferable]);
  }
  static async decryptDdocTitle(
    title: string,
    ddocId: string,
    nonce: string,
    linkKey: string
  ) {
    const worker = await this.getWorker();
    return Comlink.transfer(
      worker.decryptDdocTitle(title, ddocId, nonce, linkKey),
      [title, ddocId, nonce, linkKey]
    );
  }
  static async canSkipPublish(ddoc: ExtendedDoc): Promise<boolean> {
    if (!ddoc) return true;
    const worker = await this.getWorker();
    return Comlink.transfer(worker.canSkipPublish(ddoc), [
      ddoc as unknown as Transferable,
    ]);
  }
  static async decryptTitle(ownerLock: Lock, encryptedTitle: string) {
    if (!ownerLock || !encryptedTitle || !ownerLock?.lockedFileKey)
      return encryptedTitle;
    const privateKey = KeyStoreInstance.getDecryptionKey('owner');
    const worker = await this.getWorker();
    return Comlink.transfer(
      worker.decryptTitle(ownerLock, encryptedTitle, privateKey),
      [ownerLock, encryptedTitle, privateKey]
    );
  }
  static async encryptRSA(data: Record<any, any> | string, publicKey: string) {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.encryptRSA(data, publicKey), [
      data,
      publicKey,
    ]);
  }
  static async decryptRSA(data: string, privateKey: string) {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.decryptRSA(data, privateKey), [
      data,
      privateKey,
    ]);
  }
  static async decryptUpdatesFromHistoryViaWorker(
    updatesFromHistory: Update[],
    wsKeyMaterial: Uint8Array
  ) {
    const worker = await this.getWorker();
    return Comlink.transfer(
      worker.decryptRtcUpdatesFromHistory(updatesFromHistory, wsKeyMaterial),
      [updatesFromHistory, wsKeyMaterial]
    );
  }
  static async decryptRtcUpdatesViaWorker(
    updates: string,
    key: Uint8Array,
    nonce: Uint8Array
  ) {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.decryptRtcUpdate(updates, key, nonce), [
      updates,
      key,
      nonce,
    ]);
  }
  static async encryptRtcUpdatesViaWorker(
    updates: string,
    key: Uint8Array,
    nonce: Uint8Array
  ) {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.encryptRtcUpdate(updates, key, nonce), [
      updates,
      key,
      nonce,
    ]);
  }
  static async uploadToIPFS(
    payload: File | Record<any, any>,
    name: string,
    fileTags: string[]
  ) {
    const token = await KeyStoreInstance.getAuthToken();
    const contractAddress = KeyStoreInstance.getPortalAddress();
    const invoker = AgentInstance.getAgentAddress();
    const worker = await this.getWorker();
    return Comlink.transfer(
      worker.uploadToIPFS(
        payload,
        name,
        contractAddress,
        invoker,
        token,
        fileTags
      ),
      [payload, name, contractAddress, invoker, token, fileTags]
    );
  }
  static async getIPFSAssetSequential(ipfsHash: string) {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.getIPFSAssetSequential(ipfsHash), [
      ipfsHash,
    ]);
  }
  static async generateEncryptionMaterial(
    opts: GenerateEncryptionMaterialParams
  ): Promise<GenerateEncryptionMaterialResult> {
    const worker = await this.getWorker();
    return Comlink.transfer(worker.generateEncryptionMaterial(opts), [opts]);
  }
  static dispose(): void {
    this.cleanup();
  }
}
